import type { ChangeRequest, JobDescription, Level, JdStatus, VersionEntry } from '../../types'

export const FAMILIES = [
  'Data Engineering',
  'Machine Learning',
  'Robotics',
  'Perception',
  'Software Engineering',
]

const RESP: Record<string, string[]> = {
  'Data Engineering': [
    'Design and operate batch and streaming pipelines for fleet telemetry.',
    'Model analytics-ready datasets and own their contracts with downstream teams.',
    'Instrument data quality checks, freshness SLAs and lineage.',
    'Partner with ML teams on feature availability and backfills.',
  ],
  'Machine Learning': [
    'Own model architecture, training and evaluation for a production surface.',
    'Build reproducible training pipelines and experiment tracking.',
    'Drive offline-to-online metric alignment with the product teams.',
    'Review model changes and mentor engineers on ML practice.',
  ],
  Robotics: [
    'Develop control, state estimation and hardware integration for the platform.',
    'Take features from simulation through on-vehicle validation.',
    'Debug field issues with logs, replay and bench testing.',
    'Define interfaces between autonomy modules and vehicle hardware.',
  ],
  Perception: [
    'Build detection, tracking and classification models across camera, lidar and radar.',
    'Own dataset curation, labelling specs and failure-case mining.',
    'Optimise models for on-vehicle latency and compute budgets.',
    'Define perception KPIs and regression gates for releases.',
  ],
  'Software Engineering': [
    'Build and maintain services and tooling used across the autonomy org.',
    'Own scenario generation, regression suites and release tooling.',
    'Improve build, test and deployment reliability.',
    'Write design docs and review code across team boundaries.',
  ],
}

const SKILLS: Record<string, string[]> = {
  'Data Engineering': ['Python', 'Spark', 'Kafka', 'dbt', 'Airflow', 'SQL / warehousing'],
  'Machine Learning': ['PyTorch', 'Python', 'Distributed training', 'CUDA', 'MLOps', 'Model evaluation'],
  Robotics: ['C++17', 'ROS 2', 'Control theory', 'State estimation', 'Real-time systems', 'CAN / vehicle bus'],
  Perception: ['C++17', 'PyTorch', 'Lidar / camera calibration', 'Multi-object tracking', 'TensorRT', 'Sensor fusion'],
  'Software Engineering': ['C++ or Python', 'Simulation frameworks', 'CI/CD', 'Docker', 'Distributed systems', 'Testing at scale'],
}

const QUALS: Record<Level, string[]> = {
  Junior: [
    'Bachelor’s in CS, EE, Robotics or related field.',
    '0–2 years of relevant engineering experience.',
    'Comfortable working with guidance from senior engineers.',
  ],
  Mid: [
    'Bachelor’s or Master’s in CS, EE, Robotics or related field.',
    '3–5 years shipping production software.',
    'Track record owning a component end to end.',
  ],
  Senior: [
    'Bachelor’s or Master’s in a relevant technical field.',
    '6–9 years of industry experience, including safety-critical or real-time systems.',
    'Has led a multi-quarter technical workstream.',
  ],
  Staff: [
    'Master’s or PhD preferred, or equivalent industry depth.',
    '9–13 years of experience with deep specialisation in the domain.',
    'Sets technical direction across several teams without formal authority.',
  ],
  Principal: [
    'PhD or equivalent record of technical leadership.',
    '13+ years including ownership of a major system in production.',
    'Recognised authority who shapes multi-year architecture bets.',
  ],
}

/** Industry is derived from the family, never typed in (FR-1a). */
export const industryFor = (family: string) =>
  family === 'Robotics' || family === 'Perception' ? 'Automotive' : 'Technology'

type Spec = [string, string, string, Level, string, number, number, number, JdStatus, string, string]

const SPEC: Spec[] = [
  ['jd1', 'Senior Data Engineer', 'Data Engineering', 'Senior', 'Austin, TX', 175, 205, 3, 'Published', '2026-05-18', 'Owns the batch and streaming pipelines that turn raw fleet telemetry into analytics-ready datasets.'],
  ['jd2', 'Staff ML Engineer', 'Machine Learning', 'Staff', 'Seattle, WA', 240, 290, 4, 'Published', '2026-06-02', 'Leads model architecture and training infrastructure for the behaviour prediction stack.'],
  ['jd3', 'Robotics Perception Engineer', 'Perception', 'Mid', 'Pittsburgh, PA', 145, 172, 2, 'In review', '2026-05-04', 'Builds and tunes camera and lidar perception components for the Pittsburgh autonomy team.'],
  ['jd4', 'Senior Robotics Engineer', 'Robotics', 'Senior', 'Detroit, MI', 150, 178, 3, 'In review', '2026-04-29', 'Develops motion control and hardware integration for production robotics platforms.'],
  ['jd5', 'Senior Perception Engineer', 'Perception', 'Senior', 'Boston, MA', 205, 240, 2, 'Published', '2026-06-21', 'Owns multi-sensor detection and tracking models running on the vehicle.'],
  ['jd6', 'Principal Autonomy Architect', 'Robotics', 'Principal', 'Seattle, WA', 285, 340, 2, 'Published', '2026-03-11', 'Sets technical direction for the full autonomy stack across perception, prediction and planning.'],
  ['jd7', 'Junior Data Engineer', 'Data Engineering', 'Junior', 'Austin, TX', 105, 125, 1, 'Published', '2026-02-07', 'Supports pipeline operations and data quality checks under senior guidance.'],
  ['jd8', 'ML Infrastructure Engineer', 'Machine Learning', 'Senior', 'Boston, MA', 185, 215, 2, 'In review', '2026-05-30', 'Runs the training clusters, feature store and experiment tooling for the ML organisation.'],
  ['jd9', 'Motion Planning Engineer', 'Robotics', 'Mid', 'Austin, TX', 140, 165, 1, 'Published', '2026-01-22', 'Implements trajectory generation and behaviour planning for dense urban driving.'],
  ['jd10', 'Principal ML Scientist', 'Machine Learning', 'Principal', 'Seattle, WA', 265, 315, 2, 'Published', '2026-04-14', 'Directs applied research on end-to-end driving models and evaluation methodology.'],
  ['jd11', 'Sensor Fusion Engineer', 'Perception', 'Staff', 'Detroit, MI', 190, 225, 3, 'Published', '2026-06-09', 'Owns the fusion layer that combines radar, lidar and vision into a single world model.'],
  ['jd12', 'Simulation Engineer', 'Software Engineering', 'Mid', 'Austin, TX', 130, 155, 1, 'Draft', '2026-07-03', 'Builds scenario generation and regression simulation for autonomy releases.'],
]

const NOTES = [
  'Initial publication',
  'Scope and skills refreshed',
  'Level expectations clarified',
  'Approved employee edit request',
]

const historyFor = (version: number, updated: string): VersionEntry[] => {
  const out: VersionEntry[] = []
  for (let i = 1; i <= version; i++) {
    out.push({
      version: i,
      date: i === version ? updated : `2025-${String(i + 4).padStart(2, '0')}-1${i}`,
      note: NOTES[(i - 1) % NOTES.length],
      by: i === 1 ? 'M. Okafor' : 'L. Reyes',
    })
  }
  return out.reverse()
}

export const seedJds = (): JobDescription[] =>
  SPEC.map((s) => ({
    id: s[0],
    title: s[1],
    family: s[2],
    level: s[3],
    location: s[4],
    payMin: s[5],
    payMax: s[6],
    version: s[7],
    status: s[8],
    updated: s[9],
    summary: s[10],
    industry: industryFor(s[2]),
    responsibilities: RESP[s[2]].slice(),
    skills: SKILLS[s[2]].slice(),
    quals: QUALS[s[3]].slice(),
    createdBy: 'M. Okafor',
    updatedBy: 'L. Reyes',
  }))

export const seedVersions = (): Record<string, VersionEntry[]> => {
  const out: Record<string, VersionEntry[]> = {}
  for (const s of SPEC) out[s[0]] = historyFor(s[7], s[9])
  return out
}

export const seedRequests = (): ChangeRequest[] => [
  {
    id: 'cr1',
    jdId: 'jd4',
    jdTitle: 'Senior Robotics Engineer',
    requestedBy: 'Marcus Lindqvist',
    requestedByRole: 'Engineering Manager',
    requestedAt: '2026-07-21',
    status: 'pending',
    baseVersion: 3,
    reason:
      'My team finished the ROS 2 migration two quarters ago, and the JD still describes ROS 1 work, and it is putting off the candidates we want.',
    changes: {
      skills: ['C++17', 'ROS 2', 'Control theory', 'State estimation', 'Real-time systems', 'Drive-by-wire integration'],
      responsibilities: [
        'Develop control, state estimation and hardware integration for the platform.',
        'Own the ROS 2 migration of legacy control nodes.',
        'Take features from simulation through on-vehicle validation.',
        'Debug field issues with logs, replay and bench testing.',
      ],
    },
  },
  {
    id: 'cr2',
    jdId: 'jd3',
    jdTitle: 'Robotics Perception Engineer',
    requestedBy: 'Anika Raghunathan',
    requestedByRole: 'Perception Engineer',
    requestedAt: '2026-07-24',
    status: 'pending',
    baseVersion: 2,
    reason:
      'The role now covers radar as well as camera and lidar, and we support the Detroit team on calibration.',
    changes: {
      summary:
        'Builds and tunes camera, lidar and radar perception components for the Pittsburgh autonomy team, with shared calibration ownership across sites.',
      skills: ['C++17', 'PyTorch', 'Lidar / camera / radar calibration', 'Multi-object tracking', 'TensorRT', 'Sensor fusion'],
    },
  },
  {
    id: 'cr3',
    jdId: 'jd8',
    jdTitle: 'ML Infrastructure Engineer',
    requestedBy: 'Priya Venkatesan',
    requestedByRole: 'ML Infrastructure Engineer',
    requestedAt: '2026-07-26',
    status: 'pending',
    baseVersion: 2,
    reason:
      'Kubernetes and Ray are the day-to-day reality of this job; the qualifications also over-index on years.',
    changes: {
      skills: ['PyTorch', 'Python', 'Kubernetes', 'Ray', 'Distributed training', 'MLOps'],
      quals: [
        'Bachelor’s or Master’s in a relevant technical field.',
        '5+ years running ML training infrastructure in production.',
        'Has owned a shared platform used by other engineers.',
      ],
    },
  },
]
